import { useState, useRef, useEffect, useMemo } from 'react';
import type { ReactElement } from 'react';
import { CandleType } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';
import { hexToRgba } from '@/utils/color';
import { HeaderPopover } from './HeaderPopover';

type CandleStyle = CandleType | 'hlc';

const DEFAULT_NO_CHANGE_COLOR = '#9598a1';

/* ── Per-type SVG icons ─────────────────────────────────────────────────────── */

function CandleSolidIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden>
      {/* left candle — up (filled) */}
      <line x1="6" y1="2" x2="6" y2="5"/>
      <rect x="3.5" y="5" width="5" height="9" rx="0.5" fill="currentColor" stroke="none"/>
      <line x1="6" y1="14" x2="6" y2="17"/>
      {/* right candle — down (filled) */}
      <line x1="16" y1="5" x2="16" y2="8"/>
      <rect x="13.5" y="8" width="5" height="9" rx="0.5" fill="currentColor" stroke="none" opacity="0.45"/>
      <line x1="16" y1="17" x2="16" y2="20"/>
    </svg>
  );
}

function CandleHollowIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden>
      {/* left candle — up (hollow) */}
      <line x1="6" y1="2" x2="6" y2="5"/>
      <rect x="3.5" y="5" width="5" height="9" rx="0.5"/>
      <line x1="6" y1="14" x2="6" y2="17"/>
      {/* right candle — down (hollow, dashed border feel via opacity) */}
      <line x1="16" y1="5" x2="16" y2="8"/>
      <rect x="13.5" y="8" width="5" height="9" rx="0.5" opacity="0.45"/>
      <line x1="16" y1="17" x2="16" y2="20"/>
    </svg>
  );
}

function OhlcIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden>
      {/* left bar — up */}
      <line x1="6" y1="4" x2="6" y2="17"/>
      <line x1="3.5" y1="10" x2="6" y2="10"/>   {/* open tick left */}
      <line x1="6" y1="7" x2="8.5" y2="7"/>      {/* close tick right */}
      {/* right bar — down */}
      <line x1="16" y1="6" x2="16" y2="19"/>
      <line x1="13.5" y1="9" x2="16" y2="9"/>   {/* open tick left */}
      <line x1="16" y1="14" x2="18.5" y2="14"/> {/* close tick right */}
    </svg>
  );
}

function AreaIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="2 17 7 9 12 13 17 5 22 10" strokeWidth="1.75"/>
      <path d="M2 17 L7 9 L12 13 L17 5 L22 10 L22 20 L2 20 Z" fill="currentColor" opacity="0.18" stroke="none"/>
    </svg>
  );
}

function HlcIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden>
      {/* left bar — high(top) to low(bottom), close tick on right */}
      <line x1="6" y1="4" x2="6" y2="17"/>
      <line x1="6" y1="9" x2="9.5" y2="9"/>
      {/* right bar — high(top) to low(bottom), close tick on right */}
      <line x1="16" y1="6" x2="16" y2="20"/>
      <line x1="16" y1="14" x2="19.5" y2="14"/>
    </svg>
  );
}

/* ── Type definitions ───────────────────────────────────────────────────────── */

interface CandleTypeItem {
  value: CandleStyle;
  label: string;
  Icon: ({ size }: { size?: number }) => ReactElement;
}

const CANDLE_TYPES: CandleTypeItem[] = [
  { value: CandleType.CandleSolid,  label: 'Candles',   Icon: CandleSolidIcon  },
  { value: CandleType.CandleStroke, label: 'Hollow',    Icon: CandleHollowIcon },
  { value: CandleType.Ohlc,         label: 'OHLC Bars', Icon: OhlcIcon         },
  { value: CandleType.Area,         label: 'Area',      Icon: AreaIcon         },
  { value: 'hlc',                   label: 'HLC Bars',  Icon: HlcIcon          },
];

/* ── Component ──────────────────────────────────────────────────────────────── */

export function CandleTypeSelector() {
  const chart               = useChartStore((s) => s.chart);
  const setActiveCandleType = useChartStore((s) => s.setActiveCandleType);

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const initType = useMemo<CandleStyle>(() => {
    if (!chart) return CandleType.CandleSolid;
    const hasHLC = chart.getIndicatorByPaneId('candle_pane', 'HLC');
    if (hasHLC) return 'hlc';
    return (chart.getStyles()?.candle?.type as CandleStyle) ?? CandleType.CandleSolid;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [active, setActive] = useState<CandleStyle>(initType);

  useEffect(() => {
    if (!open) return;
    function handler(e: PointerEvent) {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [open]);

  function applyType(type: CandleStyle) {
    setActive(type);
    setActiveCandleType(type);
    setOpen(false);
    const hlcUp = useChartStore.getState().hlcUpColor;
    const hlcDown = useChartStore.getState().hlcDownColor;
    if (type === 'hlc') {
      chart?.removeIndicator('candle_pane', 'HLC');
      chart?.setStyles({
        candle: { type: CandleType.Area, area: { lineColor: 'rgba(0,0,0,0)', backgroundColor: 'rgba(0,0,0,0)' } },
      });
      (chart as unknown as { createIndicator: (n: string, s: boolean, o: { id: string }) => void })
        ?.createIndicator('HLC', true, { id: 'candle_pane' });
      chart?.setStyles({ indicator: { ohlc: { upColor: hlcUp, downColor: hlcDown, noChangeColor: DEFAULT_NO_CHANGE_COLOR } } });
    } else if (type === CandleType.Area) {
      const up = useChartStore.getState().candleUpColor;
      chart?.removeIndicator('candle_pane', 'HLC');
      chart?.setStyles({
        candle: { type: CandleType.Area, area: { lineColor: up, backgroundColor: hexToRgba(up, 0.15) } },
      });
    } else {
      chart?.removeIndicator('candle_pane', 'HLC');
      chart?.setStyles({ candle: { type: type as CandleType } });
    }
    /* candle type persisted via template */
  }

  const activeItem = CANDLE_TYPES.find((t) => t.value === active) ?? CANDLE_TYPES[0];

  return (
    <div className="relative shrink-0" ref={ref}>
      {/* Toolbar button — icon only */}
      <button
        onClick={() => setOpen((v) => !v)}
        title={activeItem.label}
        className={`flex items-center justify-center p-1.5 rounded transition-colors ${
          open ? 'bg-accent/20 text-accent' : 'text-muted hover:text-text hover:bg-panel-hover'
        }`}
      >
        <activeItem.Icon size={22} />
      </button>

      {/* Dropdown — icon + label */}
      <HeaderPopover
        ref={panelRef}
        anchorRef={ref}
        open={open}
        align="left"
        className="mt-1 w-44 glass-modal rounded-xl z-dropdown py-1.5 animate-menu-enter"
      >
        {CANDLE_TYPES.map(({ value, label, Icon }) => (
          <button
            key={value}
            onClick={() => applyType(value)}
            className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
              active === value
                ? 'text-accent bg-accent/8'
                : 'text-modal-muted hover:text-modal-text hover:bg-modal-hover'
            }`}
          >
            <Icon size={18} />
            <span className={active === value ? 'font-medium' : ''}>{label}</span>
          </button>
        ))}
      </HeaderPopover>
    </div>
  );
}

